/**
 * Qué decirle a la persona cuando falla el acceso con Google.
 *
 * Antes, `Login.js` mostraba el mismo `alert` para cualquier error, incluido el de quien
 * simplemente cerró la ventana de Google. Tres casos merecen un texto propio y dos no
 * merecen ninguno:
 *
 *   - `account-exists-with-different-credential`: el correo ya tiene cuenta creada con el
 *     código por correo. La salida es entrar por ese camino, y decirlo es lo único útil.
 *   - `popup-blocked`: el navegador no dejó abrir la ventana. Hay algo que la persona puede
 *     hacer (permitirla) y una alternativa que no depende de ventanas (el código).
 *   - `popup-closed-by-user` / `cancelled-popup-request`: la persona canceló, o un segundo
 *     clic reemplazó al primero. No es un fallo: un mensaje de error aquí es ruido.
 *
 * Función PURA y en CommonJS para poder probarla desde `functions/__tests__`, que es la
 * única suite que corre en el pre-commit (los tests de `src/` no corren en ningún lado).
 *
 * @param {{code?: string}|null|undefined} error  error de Firebase Auth
 * @returns {string|null} clave i18n del namespace `common`, o null si no hay que decir nada
 */
const SILENCIOSOS = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
]);

const CON_TEXTO_PROPIO = {
  'auth/account-exists-with-different-credential': 'login.googleAccountExists',
  'auth/popup-blocked': 'login.googlePopupBlocked',
};

function claveDeErrorDeGoogle(error) {
  const code = error && typeof error.code === 'string' ? error.code : '';
  if (SILENCIOSOS.has(code)) return null;
  return CON_TEXTO_PROPIO[code] || 'login.googleSignInError';
}

/**
 * El correo que Firebase adjunta al error de cuenta existente, si viene. Con la protección
 * de enumeración de correos activa en el proyecto puede no venir: entonces no se prerrellena
 * nada y el texto sigue sirviendo.
 */
function correoDelError(error) {
  const email = error && error.customData && error.customData.email;
  return typeof email === 'string' && email.includes('@') ? email : null;
}

module.exports = { claveDeErrorDeGoogle, correoDelError };
